import React, { useState } from "react";
import { X, Trash2 } from "lucide-react";
import Modal from "./Modal";
import Button from "@/components/Button";

function DetailField({ label, value, multiline, link }) {
  return (
    <div className="mb-3">
      <p className="text-[11px] uppercase tracking-wider font-extrabold text-sprouts-muted">{label}</p>
      {link ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sprouts-primary font-bold text-sm break-all hover:underline"
        >
          {value}
        </a>
      ) : (
        <p
          className={`text-sm font-semibold text-sprouts-text mt-1 ${
            multiline ? "whitespace-pre-wrap leading-relaxed" : ""
          }`}
        >
          {value}
        </p>
      )}
    </div>
  );
}

export default function EntryDetailModal({ entry, onClose, onDelete }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  if (!entry) return null;

  const doDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(entry);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal onClose={onClose} size="lg" testid="entry-detail-modal">
      <div className="flex items-start justify-between gap-4 mb-4">
        <h3 className="text-lg sm:text-xl font-extrabold text-sprouts-text flex-1">{entry.title}</h3>
        <button
          type="button"
          onClick={onClose}
          data-testid="entry-modal-close"
          className="p-1.5 rounded-md hover:bg-slate-100"
          aria-label="Close"
        >
          <X size={22} />
        </button>
      </div>
      <DetailField label="Student" value={entry.student_full_name} />
      <DetailField label="School" value={`${entry.school_name} (${entry.city})`} />
      <DetailField label="Grade / Section" value={`${entry.grade}-${entry.section}`} />
      <DetailField label="Contact" value={entry.parent_contact} />
      {entry.student_email && <DetailField label="Student Email" value={entry.student_email} />}
      <DetailField label="Type" value={entry.entry_type} />
      {entry.story_text && <DetailField label="Story" value={entry.story_text} multiline />}
      {entry.external_link && <DetailField label="Link" value={entry.external_link} link />}
      {entry.file_name && <DetailField label="File" value={entry.file_name} />}
      <DetailField label="Submitted" value={new Date(entry.created_at).toLocaleString()} />

      {/* Delete entry — gated behind a confirm step */}
      {onDelete && (
        <div className="mt-5 pt-5 border-t border-sprouts-border">
          {!confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              data-testid="entry-delete-trigger"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-rose-50 text-sprouts-secondary border border-rose-200 text-sm font-extrabold hover:bg-rose-100"
            >
              <Trash2 size={14} />
              Delete this entry
            </button>
          ) : (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
              <p className="text-sm font-extrabold text-sprouts-text">
                Permanently delete this entry?
              </p>
              <p className="text-xs text-sprouts-muted mt-1.5 leading-relaxed">
                This removes <strong>"{entry.title}"</strong> by {entry.student_full_name} from the
                database. The dashboard counter will go down by 1. This cannot be undone.
              </p>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  data-testid="entry-delete-cancel"
                  disabled={deleting}
                  className="px-4 py-2 rounded-full text-xs font-bold text-sprouts-muted hover:bg-white disabled:opacity-50"
                >
                  Cancel
                </button>
                <Button
                  variant="danger"
                  onClick={doDelete}
                  loading={deleting}
                  data-testid="entry-delete-confirm"
                >
                  {deleting ? "Deleting..." : "Delete permanently"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
